import React, { useState } from 'react';
import { Box, Button, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';

import PageTitle from '@Components/PageTitle/PageTitle';
import LoadingComponent from '@Components/LoadingComponent/LoadingComponent';
import * as DefaultRoutes from '@Routes/DefaultRoutes';
import themeCreator from '@Themes/themeCreator';
import { StyledCard, SectionTitle } from '@Pages/RegulatoryMember/ExitRequestDetail/styles';
import { sectionTitleStyles } from '@Pages/RnDMember/ExitRequestResponseUnified/ExitRequestResponseStyles';
import { PlanViewerWithPopup } from '@Pages/RegulatoryMember/ExitRequestDetail/PlanViewerWithPopup';
import { AffectedProductsTab } from '@Pages/RnDMember/ExitPlanForm/Tabs/AffectedProductsTab/AffectedProductsTab';
import { PlanStatusTab } from '@Pages/RnDMember/ExitPlanForm/Tabs/PlanStatusTab/PlanStatusTab';
import { getPlanStatusLabel, EDITABLE_PLAN_STATUSES, PLAN_TYPES } from '@Utils/planType';
import { getExemptionStatusColor } from '@Pages/Exemptions/ExemptionsHub/exemptionColumns';

import { useExemptionRequest } from './useExemptionRequest';
import { LocalRiskAssessmentSection } from './LocalRiskAssessmentSection';
import { ApprovalActionsSection } from './ApprovalActionsSection';
import { ExemptionPdfDialog } from './ExemptionPdfDialog';

const NA = 'N/A';
const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : NA);
const withType = (date, type) => (date ? `${formatDate(date)}${type ? ` (${type.replace(/_/g, ' ')})` : ''}` : NA);
const userLabel = (user) => (user ? `${user.username || ''}${user.email ? ` (${user.email})` : ''}` : NA);

// The page reads like the printed request: one column of cards that size to their content.
// Deliberately avoids the shared PageContainer/cardTransition styles, whose height:100% and
// 90vh minimum stretch a short card over the whole viewport.
const pageStyles = (theme) => ({
  padding: theme.spacing(2, 1.5, 4, 1.5),
  [theme.breakpoints.up('md')]: { padding: theme.spacing(2, 3, 4, 3) },
  maxWidth: 1400,
  margin: '0 auto',
});

const headerStyles = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 2,
  mb: 2,
};

// Label/value pairs flow into as many columns as fit, so nothing is left ragged or stranded.
const infoGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  columnGap: 3,
  rowGap: 1.5,
};

const textBlockGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 2,
};

const InfoItem = ({ label, value, wide }) => (
  <Box sx={{ minWidth: 0, gridColumn: wide ? '1 / -1' : 'auto' }}>
    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
      {value || NA}
    </Typography>
  </Box>
);

const Section = ({ title, theme, action, children }) => (
  <StyledCard sx={{ mb: 0 }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <SectionTitle variant="h6" sx={{ ...sectionTitleStyles(theme), mb: 0, pb: 0, borderBottom: 'none' }}>
          {title}
        </SectionTitle>
        {action}
      </Box>
      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 1, pt: 2 }}>{children}</Box>
    </CardContent>
  </StyledCard>
);

/** One read-only Exemption Request page for every role; actions appear per role and status. */
const ExemptionRequestPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const theme = themeCreator();
  const userId = useSelector((state) => state.user.userId);
  const role = useSelector((state) => state.user.role);
  const { plan, loading, acting, reload, submitLocalRiskAssessment, decide } = useExemptionRequest(planId);
  const [pdfOpen, setPdfOpen] = useState(false);

  if (loading || !plan) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <LoadingComponent size="5em" thickness={5} color="primary" />
      </Box>
    );
  }

  const isExemptionRecord = plan.planType === PLAN_TYPES.EXEMPTION_REQUEST;
  const isRequestor = String(plan.submittedBy?.id) === String(userId);
  const isRegulatoryOwner = String(plan.regulatoryOwner?.id) === String(userId);
  const canEdit = isRequestor && EDITABLE_PLAN_STATUSES.includes(plan.status);
  const canSubmitRiskAssessment = isRegulatoryOwner && plan.status === 'PENDING_LOCAL_RISK_ASSESSMENT';
  // Only the exit request's actual approving managers may decide, not every user holding the role.
  const isApprovingManager = (plan.approvingManagers || []).some((m) => String(m.id) === String(userId));
  const canDecide = isApprovingManager && plan.status === 'SUBMITTED';

  const editRoute = () => {
    const base = role === 'RD_MANAGER'
      ? DefaultRoutes.rdManagerExemptionCreate
      : role === 'APPROVER'
        ? DefaultRoutes.ApproverExemptionCreate
        : DefaultRoutes.rdExemptionCreate;
    return base.replace(':reqId', plan.requestId).replace(':categoryAssignmentId', plan.categoryAssignmentId);
  };

  const countryNames = plan.countries?.length ? plan.countries.map((c) => c.name).join(', ') : NA;

  return (
    <Box sx={pageStyles(theme)}>
      <Box sx={headerStyles}>
        <PageTitle
          title={`Exemption Request #${plan.id}`}
          PageIcon={<ReceiptLongIcon color="white" fontSize="large" />}
          iconColor={theme.palette.warning.main}
        />
        <Chip
          label={getPlanStatusLabel(plan.status, plan.planType)}
          color={getExemptionStatusColor(plan.status)}
          size="small"
        />
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexShrink: 0 }}>
          {canEdit && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              sx={{ textTransform: 'none' }}
              onClick={() => navigate(editRoute(), { state: { planId: plan.id } })}
            >
              Edit
            </Button>
          )}
          {isExemptionRecord && (
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={<PictureAsPdfIcon />}
              sx={{ textTransform: 'none' }}
              onClick={() => setPdfOpen(true)}
            >
              Print / PDF
            </Button>
          )}
          <Button variant="outlined" size="small" sx={{ textTransform: 'none' }} onClick={() => navigate(-1)}>
            Back
          </Button>
        </Box>
      </Box>

      {!isExemptionRecord && (
        <Typography color="error" sx={{ mb: 2 }}>This record is an exit plan, not an exemption request.</Typography>
      )}

      <Stack spacing={2}>
        <Section title="Request Information" theme={theme}>
          <Box sx={infoGridStyles}>
            <InfoItem label="Exit Request" value={`#${plan.requestId}`} />
            <InfoItem label="Ingredient" value={plan.ingredientName} />
            <InfoItem label="CAS Number" value={plan.ingredientCasNumber} />
            <InfoItem label="Restriction" value={(plan.restrictionType || NA).replace(/_/g, ' ')} />
            <InfoItem label="BG (Main Category)" value={plan.businessGroup} />
            <InfoItem label="Categories (Sub Category)" value={plan.categoryName} />
            <InfoItem label="Market / Region" value={plan.regions?.length ? plan.regions.join(', ') : NA} />
            <InfoItem label="Requestor" value={userLabel(plan.regulatoryOwner)} />
            <InfoItem label="Submitted by (R&D)" value={userLabel(plan.submittedBy)} />
            <InfoItem label="Country(ies)" value={countryNames} wide />
          </Box>
        </Section>

        <Section title="Deadlines & Proposed Implementation" theme={theme}>
          <Box sx={infoGridStyles}>
            <InfoItem label="Regulatory deadline - 1ry date" value={withType(plan.primaryDate, plan.primaryDateType)} />
            <InfoItem label="Regulatory deadline - 2ry date" value={withType(plan.secondaryDate, plan.secondaryDateType)} />
            <InfoItem label="Internal deadline (UL)" value={formatDate(plan.ulInternalDate)} />
            <InfoItem label="Proposed stop production" value={formatDate(plan.endOfProductionDate)} />
            <InfoItem label="Proposed stop importation" value={formatDate(plan.endOfImportationDate)} />
          </Box>
        </Section>

        <Section title="Exemption Details" theme={theme}>
          <Box sx={infoGridStyles}>
            <InfoItem label="Exception start date" value={formatDate(plan.exceptionStartDate)} />
            <InfoItem label="Exception end date" value={formatDate(plan.exceptionEndDate)} />
            <InfoItem label="TO impact (Euros)" value={plan.toImpactEuros == null ? NA : `${plan.toImpactEuros} EUR`} />
            <InfoItem label="Pharos/Pelican safety assessment ref." value={plan.safetyAssessmentReference} />
            <InfoItem label="Product types" value={plan.productTypes} />
            <InfoItem label="Brands" value={plan.brands} />
          </Box>
          {/* Compact boxes side by side; each expands to a full dialog via its own icon. */}
          <Box sx={{ ...textBlockGridStyles, mt: 1 }}>
            <PlanViewerWithPopup title="Extension or Exemption Request" content={plan.liquidationPlan || NA} height={120} />
            <PlanViewerWithPopup title="Justification" content={plan.justification || NA} height={120} />
            <PlanViewerWithPopup title="Additional Comments" content={plan.additionalComments || 'No Additional Info available'} height={120} />
          </Box>
        </Section>

        <Section title={`Impacted Products (${plan.products?.length || 0})`} theme={theme}>
          <AffectedProductsTab affectedProducts={plan.products || []} theme={theme} readOnly={true} />
        </Section>

        <Section title="Local Regulatory Risk Assessment" theme={theme}>
          <LocalRiskAssessmentSection
            plan={plan}
            canSubmit={canSubmitRiskAssessment}
            submitting={acting}
            onSubmit={submitLocalRiskAssessment}
          />
        </Section>

        <Section title="LT Approval" theme={theme}>
          <PlanStatusTab
            createdAt={plan.createdAt}
            lastUpdatedAt={plan.lastUpdatedAt}
            status={plan.status}
            approved={plan.approved}
            approvedBy={plan.approvedBy}
            approvalDate={plan.approvalDate}
            approvalComment={plan.approvalComment}
            approvals={plan.approvals || []}
            approvingManagers={plan.approvingManagers || []}
            totalApprovers={plan.totalApprovers || 0}
            approvedCount={plan.approvedCount || 0}
            planType={plan.planType}
            theme={theme}
          />
          {canDecide && (
            <Box sx={{ mt: 2 }}>
              <ApprovalActionsSection
                plan={plan}
                userId={userId}
                canDecide={canDecide}
                acting={acting}
                onDecide={decide}
                onApproverAdded={reload}
              />
            </Box>
          )}
        </Section>
      </Stack>

      <ExemptionPdfDialog open={pdfOpen} onClose={() => setPdfOpen(false)} planId={plan.id} />
    </Box>
  );
};

export default ExemptionRequestPage;
